import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { API } from "@/config";

export const TrialForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [customers, setCustomers] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: "",
    device_name: "",
    serial_number: "",
    estimated_value: "",
    trial_start: new Date().toISOString().split('T')[0],
    trial_end: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    pre_existing_notes: "",
    sales_agent: "",
    notes: ""
  });

  useEffect(() => {
    fetchCustomers();
    if (isEdit && id) fetchTrial();
  }, [id, isEdit]);

  const fetchCustomers = async () => {
    try { const response = await axios.get(`${API}/customers`); setCustomers(response.data); }
    catch (error) { toast.error("Error fetching customers"); }
  };

  const fetchTrial = async () => {
    try {
      const response = await axios.get(`${API}/trials/${id}`);
      const d = response.data;
      setFormData({
        customer_id: d.customer_id,
        device_name: d.device_name,
        serial_number: d.serial_number || "",
        estimated_value: d.estimated_value?.toString() || "",
        trial_start: d.trial_start ? d.trial_start.split('T')[0] : "",
        trial_end: d.trial_end ? d.trial_end.split('T')[0] : "",
        pre_existing_notes: d.pre_existing_notes || "",
        sales_agent: d.sales_agent || "",
        notes: d.notes || ""
      });
    } catch (error) { toast.error("Error fetching trial agreement"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) { toast.error("Please select a customer"); return; }
    if (!formData.device_name) { toast.error("Please enter a device name"); return; }

    try {
      const submitData = {
        ...formData,
        estimated_value: parseFloat(formData.estimated_value) || 0,
        trial_start: new Date(formData.trial_start).toISOString(),
        trial_end: new Date(formData.trial_end).toISOString()
      };
      if (isEdit) {
        await axios.put(`${API}/trials/${id}`, submitData);
        toast.success("Trial agreement updated");
      } else {
        await axios.post(`${API}/trials`, submitData);
        toast.success("Trial agreement created");
      }
      navigate('/trials');
    } catch (error) { toast.error("Error saving trial agreement"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold" data-testid="trial-form-title">
          {isEdit ? "Edit Trial Agreement" : "New Demo Device Trial Agreement"}
        </h1>
        <Button variant="outline" onClick={() => navigate('/trials')} data-testid="back-btn">Back</Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader><CardTitle>Customer Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Select Customer</Label>
              <Select value={formData.customer_id} onValueChange={(value) => setFormData({ ...formData, customer_id: value })}>
                <SelectTrigger data-testid="trial-customer-select"><SelectValue placeholder="Select a customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Sales Agent</Label>
              <Input value={formData.sales_agent} onChange={(e) => setFormData({ ...formData, sales_agent: e.target.value })} placeholder="Agent name" data-testid="trial-sales-agent-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Equipment Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Device Name / Model *</Label>
                <Input value={formData.device_name} onChange={(e) => setFormData({ ...formData, device_name: e.target.value })} placeholder="e.g., Pico Laser / Body Contouring" data-testid="trial-device-name-input" required />
              </div>
              <div>
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })} placeholder="Insert S/N" data-testid="trial-serial-input" />
              </div>
            </div>
            <div>
              <Label>Estimated Full Replacement Value ($)</Label>
              <Input type="number" step="0.01" value={formData.estimated_value} onChange={(e) => setFormData({ ...formData, estimated_value: e.target.value })} placeholder="0.00" data-testid="trial-value-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Trial Period</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Trial Start Date *</Label>
                <Input type="date" value={formData.trial_start} onChange={(e) => setFormData({ ...formData, trial_start: e.target.value })} data-testid="trial-start-input" required />
              </div>
              <div>
                <Label>Trial End Date *</Label>
                <Input type="date" value={formData.trial_end} onChange={(e) => setFormData({ ...formData, trial_end: e.target.value })} data-testid="trial-end-input" required />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Condition Notes</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Pre-Existing Scratches or Issues</Label>
              <Textarea value={formData.pre_existing_notes} onChange={(e) => setFormData({ ...formData, pre_existing_notes: e.target.value })} placeholder="Note any pre-existing scratches, functional issues, or cosmetic defects at time of delivery" data-testid="trial-preexisting-input" />
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional terms or notes" data-testid="trial-notes-input" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/trials')} data-testid="trial-cancel-btn">Cancel</Button>
          <Button type="submit" data-testid="save-trial-btn">{isEdit ? "Update" : "Create"} Trial Agreement</Button>
        </div>
      </form>
    </div>
  );
};
